/**
 * "Something is happening, here is what, and here is how far."
 *
 * The reported problem, in the user's words: the interview card gives no
 * in-progress indication while it works, so it reads as stuck and they press
 * Change to redo a step that was running fine.
 *
 * Four things have to be true at once for a wait to feel safe, and the old card
 * had none of them for its longest steps:
 *
 *   1. NAMED — what is it doing? A bare spinner says "wait" without saying
 *      for what, so a long wait is indistinguishable from a hang.
 *   2. MEASURED — how far? A determinate bar when a real percentage exists.
 *      Never a fabricated one: an invented bar that parks at 90% is worse than
 *      an honest indeterminate sweep, because it promises an ending it cannot
 *      deliver.
 *   3. ALIVE — the elapsed counter ticks every second. That is the part a
 *      frozen tab cannot fake; CSS animation is not evidence of anything.
 *   4. ESCAPABLE — a Stop that works. Measured on the live stack: grouping 300
 *      images takes 24.6s server-side, and auto-boxing runs one request per
 *      image. A wait that long with no exit is what sends a user hunting for
 *      any other button on the card.
 */
import React from 'react';
import { useI18n } from '../../../i18n';
import useElapsedSeconds from '../hooks/useElapsedSeconds';

const CardBusyStrip = ({ label, detail, percent, phaseKey, onStop, stopLabel, active = true }) => {
  const { t } = useI18n();
  const seconds = useElapsedSeconds(active, phaseKey);

  // A percentage is shown only when one genuinely exists. Everything else gets
  // the indeterminate sweep, which claims nothing.
  const known = typeof percent === 'number' && Number.isFinite(percent) && percent > 0;
  const value = known ? Math.max(0, Math.min(100, Math.round(percent))) : null;

  return (
    <div className="lf-busy" role="status" aria-live="polite">
      <div className="lf-busy__line">
        <i className="fa fa-circle-notch fa-spin lf-busy__spin" aria-hidden="true" />
        <span className="lf-busy__label">{label}</span>
        {value != null && <span className="lf-busy__percent">{value}%</span>}
        <span className="lf-busy__elapsed">{t('assistant.interview.status.elapsed', { seconds })}</span>
      </div>

      {detail && <p className="lf-busy__detail">{detail}</p>}

      <div
        className={`lf-busy__track${value == null ? ' lf-busy__track--indeterminate' : ''}`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        {...(value != null ? { 'aria-valuenow': value } : {})}
        aria-label={label}
      >
        <span className="lf-busy__fill" style={value != null ? { width: `${value}%` } : undefined} />
      </div>

      {onStop && (
        <button type="button" className="lf-busy__stop" onClick={onStop}>
          <i className="fa fa-stop" aria-hidden="true" /> {stopLabel}
        </button>
      )}
    </div>
  );
};

export default CardBusyStrip;
